/* @flow */
import { compose } from 'ramda';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';
import {
  getTopicAuthor,
  getGroupTopicStartTime,
  getGroupTopicSpotLeftCount,
  getGroupTopicTimeSlot,
} from '@e180/domain/lib/topic/util';
import { getConversationTopic } from '@e180/domain/lib/conversation/util';
import { getUserId, getUserAvatar } from '@e180/domain/lib/user/util';

import styles from './GroupBraindateBanner.style';
import DateTime from 'src/shared/app/base/date/component/DateTime';
import Timezone from 'src/shared/app/base/date/component/Timezone';
import ConversationAvatar from 'src/shared/app/conversation/component/avatar/ConversationAvatar';
import useVirtualBraindate from 'plugin/virtual-braindate/hook/useVirtualBraindate';
import messages from 'src/shared/domain/braindate/l10n/braindateL10n';
import { getConversationParticipantsFirstNamesAsString } from 'src/shared/domain/conversation/util/conversationUtil';
import GroupAvatar from 'src/shared/ui/component/image/GroupAvatar';

import type { Conversation } from '@e180/domain/lib/conversation/type';
import type { Topic } from '@e180/domain/lib/topic/type';
import type { User } from '@e180/domain/lib/user/type';

type Props = {
  conversation?: Conversation,
  topic?: Topic,
  participants?: Array<User>,
};

const useStyles = createUseStyles(styles);

const GroupBraindateBanner = ({
  conversation,
  topic,
  participants,
}: Props): React.Node => {
  let names;
  let parsedTopic;
  let spotsLeft;
  let startTime;
  let sortedParticipants;
  let avatarGroup;

  const intl = useIntl();
  const isVirtualBraindateEnabled = useVirtualBraindate();

  if (conversation) {
    names = getConversationParticipantsFirstNamesAsString(conversation, true);
    parsedTopic = getConversationTopic(conversation);
  } else if (Array.isArray(participants) && typeof topic === 'object') {
    const authorId = getUserId(getTopicAuthor(topic));

    sortedParticipants = [...participants];

    sortedParticipants.sort((p) => (p.id === authorId ? -1 : 1));

    names = sortedParticipants.map((p) => p.first_name).join(', ');
    avatarGroup = sortedParticipants.map(
      (p) => (getUserAvatar(p) || {})['75x75'],
    );
    parsedTopic = topic;
  }

  // If topic not cancelled
  // FIXME: don't hardoce parsedTopic.date
  // $FlowIssue
  if (parsedTopic && parsedTopic.date && getGroupTopicTimeSlot(parsedTopic)) {
    // $FlowIssue
    startTime = getGroupTopicStartTime(parsedTopic);
    // $FlowIssue
    spotsLeft = parseInt(getGroupTopicSpotLeftCount(parsedTopic), 10);
  }

  // Texts

  let dateElt = '';
  let spotElt;

  if (startTime) {
    const zoneElt = isVirtualBraindateEnabled && (
      <React.Fragment>
        {' '}
        <Timezone withoutCity />
      </React.Fragment>
    );

    dateElt = (
      <React.Fragment>
        <DateTime date={startTime} withToday />
        {zoneElt}
      </React.Fragment>
    );
  }

  if (typeof spotsLeft === 'number') {
    spotElt = intl.formatMessage(messages.spotCount, {
      spotCount: Math.max(spotsLeft, 0),
    });
  }

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  let avatarElt;

  if (conversation) {
    avatarElt = <ConversationAvatar conversation={conversation} />;
  } else if (avatarGroup) {
    avatarElt = <GroupAvatar src={avatarGroup} />;
  }

  return (
    <div className={classes.root}>
      {avatarElt}
      <div className={classes.text}>
        <span className={classes.names}>{names}</span>
        <span className={classes.details}>
          {dateElt} - {spotElt}
        </span>
      </div>
    </div>
  );
};

export default compose(React.memo)(GroupBraindateBanner);
